import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { OrderLineModel, ShipmentPackageDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import StatusLableAndHeadingOrderDetailTopStatus from "@WhiteCap/Components/StatusLableAndHeadingOrderDetailTopStatus";
import { OrderDataRealtime } from "@WhiteCap/Overrides/Widgets/OrderDetails/OrderDetailsSummaryTable";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import React, { FC, useContext } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{
        orderId?: string;
    }>(location.search);
    const orderNumber = parsedQuery.orderId ?? parsedQuery.orderId;

    return {
        orderData: orderNumber ? state.WhiteCap.customOrderStatusPage.orderStatusById[orderNumber] : null,
        pageType: getCurrentPage(state).type,
    };
};

type Props = ReturnType<typeof mapStateToProps>;

export interface OrderDetailsStatusStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyProps;
    status?: TypographyProps;
    text?: TypographyProps;
    orderLineCount?: TypographyPresentationProps;
}

export const statusStyles: OrderDetailsStatusStyles = {
    wrapper: {
        css: css`
            padding-bottom: 10px;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
            padding-bottom: 0px;
                `,
                    css`
                    padding-bottom: 0px;
                `,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    status: {
        css: css`
        font-size: 16px;
font-style: normal;
font-weight:500;
        `,
    },
    orderLineCount: {
        css: css`
        font-size: 16px;
font-style: normal;
font-weight:500;
margin-bottom: 0px;

${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
font-size: 12px;
                `,
                    css`
                font-size: 12px;
                `,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    text: {
        css: css`
            width: 100%;
            font-size: 12px !important;
            color:#fff !important;
font-style: normal;
    /* line-height:5px; */
    font-weight: 500;
    padding-top:3px;
    padding-bottom:3px;
    text-transform: capitalize;
    text-align:center !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                     font-size: 12px;
                `,
                    css`
                font-size: 12px;
                `,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    titleText: {
        variant: "h3",
        css: css`
            @media print {
                font-size: 12px;
            }
            margin-bottom: 0px;
            text-align: center;
            text-transform: uppercase;
    font-weight: 400;
font-size: 14px;
        `,
    },
};

const styles = statusStyles;

const OrderDetailsStatus: FC<Props> = ({ pageType, orderData }) => {
    const order = pageType === "CustomOrderStatusPage" ? orderData : useContext(OrderStateContext).value;

    if (!order || !order.orderLines) {
        return null;
    }

    const groupByDeliveryId = (orderLines: OrderLineModel[], shipmentPackages?: ShipmentPackageDto[] | null) => {
        const shipmentPackagesMap: { [packageNumber: string]: ShipmentPackageDto } = {};

        if (shipmentPackages) {
            shipmentPackages.forEach((shipmentPackage) => {
                shipmentPackagesMap[shipmentPackage.packageNumber.split("-")[0]] = shipmentPackage;
            });
        }

        return orderLines.reduce<{ [deliveryId: string]: OrderDataRealtime }>((grouped, orderLine) => {
            let deliveryIds: string[] = [];
            if (orderLine.properties?.deliveryId?.includes(",")) {
                deliveryIds = orderLine.properties.deliveryId.split(",").map(id => id.trim());
            } else {
                deliveryIds.push(orderLine.properties?.deliveryId?.trim());
            }

            deliveryIds.forEach((deliveryId) => {
                // Skip null or undefined deliveryIds
                if (!deliveryId) {
                    return;
                }
                if (!grouped[deliveryId]) {
                    grouped[deliveryId] = {
                        orderLine: [],
                    };
                }
                grouped[deliveryId].orderLine.push({ ...orderLine });

                const shipmentPackage = shipmentPackagesMap[deliveryId];
                if (shipmentPackage?.shipmentPackageLineDtos) {
                    shipmentPackage.shipmentPackageLineDtos.forEach((shipmentPackageLine) => {
                        const matchedOrderLine = grouped[deliveryId].orderLine.find(orderLine => orderLine.id === shipmentPackageLine.orderLineId);
                        if (matchedOrderLine) {
                            matchedOrderLine.qtyOrdered = shipmentPackageLine.qtyOrdered;
                        }
                    });
                }
            });

            return grouped;
        }, {});
    };

    const groupedOrderLines = groupByDeliveryId(order.orderLines, order.shipmentPackages);
    Object.entries(groupedOrderLines).forEach(([deliveryId, orderLines]) => {
        if (order.properties?.realtimeOrderStatus) {
            const realtimeOrderStatus = JSON.parse(order.properties?.realtimeOrderStatus);
            realtimeOrderStatus.forEach((realtimeOrderStatusdeliveryId: any) => {
                const matchedOrder = realtimeOrderStatusdeliveryId.deliveryId === deliveryId;
                if (matchedOrder) {
                    groupedOrderLines[deliveryId] = {
                        ...groupedOrderLines[deliveryId],
                        realtimeOrderStatusdeliveryId,
                    };
                }
            })
        } else if (order.shipmentPackages) {
            const realtimeOrderShipmentStatus = order.shipmentPackages;
            realtimeOrderShipmentStatus.forEach(realtimeOrderStatusdeliveryId => {
                groupedOrderLines[deliveryId] = {
                    ...groupedOrderLines[deliveryId],
                    realtimeOrderStatusdeliveryId: {
                        trackingURL: realtimeOrderStatusdeliveryId.trackingUrl,
                        shipVia: realtimeOrderStatusdeliveryId.shipVia,
                        trackingNumber: realtimeOrderStatusdeliveryId.trackingNumber,
                    }
                };
            })
        }
    });

    const shipmentStatusByDeliveryId = order?.properties?.shipmentStatus ? JSON.parse(order.properties.shipmentStatus) : {};

    return (
        <StyledWrapper {...styles.wrapper}>
            <Hidden
                above="sm"
                css={css`
                            width: 100%;
                        `}
            >
                <StyledWrapper css={css`display: none; @media print{display:block;} flex-direction: column; `}><Typography {...styles.titleText} id="orderDetailsStatus">
                    {translate("Status")}
                </Typography>
                    <StyledWrapper css={css`display: flex; justify-content: center; `}>
                        {Object.entries(groupedOrderLines).map(([deliveryId, orderLines], index) => (
                            <div key={index}>
                                {(orderLines.realtimeOrderStatusdeliveryId?.status || order.statusDisplay) && <StatusLableAndHeadingOrderDetailTopStatus
                                    heading={translate("Status")}
                                    text={
                                        orderLines.realtimeOrderStatusdeliveryId?.status ? orderLines.realtimeOrderStatusdeliveryId?.status : shipmentStatusByDeliveryId[deliveryId]
                                        ? shipmentStatusByDeliveryId[deliveryId]: order.statusDisplay
                                    }
                                />
                                }
                                {(Object.entries(groupedOrderLines).length > 1 && index + 1 !== Object.entries(groupedOrderLines).length) && <StyledWrapper css={css`margin: 0 2px`}> | </StyledWrapper>}
                            </div>
                        ))}</StyledWrapper></StyledWrapper>
                <StyledWrapper css={css`display: flex; @media print{display:none;} flex-direction: column; `}>
                    {Object.entries(groupedOrderLines).map(([deliveryId, orderLines], index) => (
                        <StyledWrapper key={index} css={css`width:100%; display: flex; padding:10px;justify-content: space-between;`}>
                            <StyledWrapper css={css`display:flex;width:100%;`}>
                                <Typography as="p" css={css`font-size: 12px; font-weight:500; width: 100%`}>{`DELIVERY ${Object.entries(groupedOrderLines).length > 1 ? `${index + 1}` : ""}`} </Typography>
                                {(orderLines.orderLine[0].properties?.shippingMethod || order.shipViaDescription) && <Typography as="p" css={css`font-size: 12px; font-weight:500; color:#28519B; width:inherit;`}> {orderLines.orderLine[0].properties?.shippingMethod ? orderLines.orderLine[0].properties?.shippingMethod : order.shipViaDescription ? order.shipViaDescription : ""}</Typography>}
                            </StyledWrapper>
                            <StatusLableAndHeadingOrderDetailTopStatus
                                heading={translate("Status")}
                                text={
                                    orderLines.realtimeOrderStatusdeliveryId?.status ? orderLines.realtimeOrderStatusdeliveryId?.status : shipmentStatusByDeliveryId[deliveryId]
                                    ? shipmentStatusByDeliveryId[deliveryId] : order.statusDisplay
                                }
                            />
                        </StyledWrapper>

                    ))}</StyledWrapper>

            </Hidden>
            <Hidden
                below="md"
                css={css`
                            width: 100%;
                        `}
            >
                <Typography {...styles.titleText} id="orderDetailsStatus">
                    {translate("Status")}
                </Typography>
                <StyledWrapper css={css`display: flex; @media print{display:block;} justify-content: center; `}>
                    {Object.entries(groupedOrderLines).length > 2 ? <>

                        <Typography {...styles.orderLineCount} aria-labelledby="orderPo">
                            {Object.entries(groupedOrderLines).length}
                        </Typography>
                    </> : <>
                        {Object.entries(groupedOrderLines).map(([deliveryId, orderLines], index) => (
                            <>
                                <div key={index}>
                                    {(orderLines.realtimeOrderStatusdeliveryId?.status || order.statusDisplay) && <StatusLableAndHeadingOrderDetailTopStatus
                                        heading={translate("Status")}
                                        text={
                                            orderLines.realtimeOrderStatusdeliveryId?.status ? orderLines.realtimeOrderStatusdeliveryId?.status : shipmentStatusByDeliveryId[deliveryId]
                                            ? shipmentStatusByDeliveryId[deliveryId] : order.statusDisplay
                                        }
                                    />}
                                </div>
                                {(Object.entries(groupedOrderLines).length > 1 && index + 1 !== Object.entries(groupedOrderLines).length) && <StyledWrapper css={css`margin: 0 10px`}> | </StyledWrapper>}
                            </>
                        ))}
                    </>}
                </StyledWrapper>
            </Hidden>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OrderDetailsStatus),
    definition: {
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage", "CustomOrderStatusPage"],
        group: "Order Details",
    },
};

export default widgetModule;
